// client/src/modals/LessonModal.jsx

import React, { useState, useEffect } from 'react';
import ResourceFileList from './ResourceFileList';
import useCourse from '../../hooks/useCourse';
import useBlobStorage from '../../hooks/useBlobStorage';
import './LessonModal.css';

const ROLE_CFG = {
  admin:      { cls: 'lm--admin',      label: 'Admin'      },
  instructor: { cls: 'lm--instructor', label: 'Instructor' },
  student:    { cls: 'lm--student',    label: 'Student'    },
};

const LessonModal = ({
  visible,
  onClose,
  role = 'student',
  lesson,
  authData,
  courseId,
  onLoad,
  onDownload,
  onUploadFiles,
  onRemoveNewFile,
  pendingFiles,
  isUploadingFiles,
  uploadProgress,
  onStageFiles,
  onAfterSave,
}) => {
  const [editing, setEditing]       = useState(false);
  const [data,    setData]          = useState(null);
  const [saving,  setSaving]        = useState(false);

  const { updateCourseContent } = useCourse();
  const { uploadFile }          = useBlobStorage();

  useEffect(() => { if (lesson) setData({ ...lesson }); }, [lesson]);
  useEffect(() => { if (!visible) setEditing(false); },    [visible]);

  if (!visible || !lesson) return null;

  const roleCfg = ROLE_CFG[role] || ROLE_CFG.student;
  const canEdit = role === 'instructor' || role === 'admin';
  const set     = (k) => (v) => setData(d => ({ ...d, [k]: v }));

  const handleSave = async () => {
    if (!data?.title?.trim()) {
      alert('Title is required');
      return;
    }
    setSaving(true);
    try {
      const { lessonId, ...updateData } = data;
      updateData.updatedBy = authData?.id;

      // Upload pending files
      if (onUploadFiles) {
        const uploadResult = await onUploadFiles('lessons', lessonId);
        if (uploadResult?.fileIds?.length > 0) {
          updateData.resourceFileIds  = [...(updateData.resourceFileIds  || []), ...uploadResult.fileIds];
          updateData.resourceFileNames = [...(updateData.resourceFileNames || []), ...uploadResult.fileNames];
        }
      }

      if (updateData.duration !== undefined) updateData.duration = Number(updateData.duration);
      if (updateData.order    !== undefined) updateData.order    = Number(updateData.order);

      const response = await updateCourseContent(courseId, 'lesson', lessonId, updateData);
      if (response.success) {
        setEditing(false);
        onAfterSave?.();
        await onLoad?.();
      } else {
        alert('Failed to save lesson');
      }
    } catch (err) {
      console.error('LessonModal save error:', err);
      alert('Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setData({ ...lesson });
    setEditing(false);
  };

  return (
    <div className="lm-overlay" onClick={onClose}>
      <div className={`lm-shell ${roleCfg.cls}`} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="lm-header">
          <div className="lm-icon">📚</div>
          <div className="lm-title-wrap">
            <div className="lm-title">{editing ? (data?.title || '') : lesson.title}</div>
            <div className="lm-sub">
              {lesson.lessonId}
              {editing && <span className="lm-edit-pill">Editing</span>}
            </div>
          </div>
          <div className="lm-hdr-actions">
            {editing ? (
              <>
                <button className="lm-btn lm-btn--ghost" onClick={handleCancel} disabled={saving}>Cancel</button>
                <button className="lm-btn lm-btn--save"  onClick={handleSave}   disabled={saving}>
                  {saving ? '⏳ Saving…' : '💾 Save'}
                </button>
              </>
            ) : (
              <>
                {canEdit && <button className="lm-btn lm-btn--edit" onClick={() => setEditing(true)}>✏ Edit</button>}
                <button className="lm-close" onClick={onClose}>✕</button>
              </>
            )}
          </div>
        </div>

        {/* Status bar */}
        <div className="lm-status">
          <span className="lm-chip">⏱ {lesson.duration} min</span>
          <span className="lm-chip lm-chip--role">{roleCfg.label}</span>
        </div>

        {/* Body */}
        <div className="lm-body">
          {editing ? (
            <div className="lm-form">
              <div className="lm-field">
                <label className="lm-label">Title <span className="lm-req">*</span></label>
                <input className="lm-input" value={data?.title || ''} onChange={e => set('title')(e.target.value)} />
              </div>
              <div className="lm-field">
                <label className="lm-label">Duration (min)</label>
                <input className="lm-input" type="number" value={data?.duration || ''} onChange={e => set('duration')(e.target.value)} />
              </div>
              <div className="lm-field">
                <label className="lm-label">Content <span className="lm-req">*</span></label>
                <textarea className="lm-textarea" rows="8" value={data?.content || ''} onChange={e => set('content')(e.target.value)} />
              </div>
              <div className="lm-field">
                <label className="lm-label">Video URL</label>
                <input className="lm-input" value={data?.videoUrl || ''} onChange={e => set('videoUrl')(e.target.value)} placeholder="https://…" />
              </div>
              <div className="lm-field">
                <label className="lm-label">Resources</label>
                <textarea className="lm-textarea" rows="3" value={data?.resources || ''} onChange={e => set('resources')(e.target.value)} />
              </div>
              <div className="lm-field">
                <label className="lm-label">📎 Files</label>
                <ResourceFileList
                  fileIds={data?.resourceFileIds   || []}
                  fileNames={data?.resourceFileNames || []}
                  onDownload={onDownload}
                  editMode
                  onRemove={(fi) => {
                    setData(d => ({
                      ...d,
                      resourceFileIds:   d.resourceFileIds.filter((_,i) => i !== fi),
                      resourceFileNames: d.resourceFileNames.filter((_,i) => i !== fi),
                    }));
                  }}
                  newFiles={pendingFiles?.['lessons'] || []}
                  onAddFiles={(files) => onStageFiles?.('lessons', files)}
                  onRemoveNew={(fi) => onRemoveNewFile?.('lessons', fi)}
                  uploading={isUploadingFiles}
                  uploadProgress={typeof uploadProgress === 'object' ? uploadProgress?.['lessons'] : uploadProgress}
                />
              </div>
            </div>
          ) : (
            <div className="lm-view">
              {lesson.content && (
                <div className="lm-section">
                  <div className="lm-section-title">Content</div>
                  <p className="lm-text">{lesson.content}</p>
                </div>
              )}
              {lesson.videoUrl && (
                <div className="lm-section">
                  <div className="lm-section-title">🎥 Video</div>
                  <a href={lesson.videoUrl} target="_blank" rel="noopener noreferrer" className="lm-link">{lesson.videoUrl}</a>
                </div>
              )}
              {(lesson.resources || lesson.resourceFileIds?.length > 0) && (
                <div className="lm-section">
                  <div className="lm-section-title">📚 Resources</div>
                  {lesson.resources && <p className="lm-text">{lesson.resources}</p>}
                  {lesson.resourceFileIds?.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <ResourceFileList
                        fileIds={lesson.resourceFileIds}
                        fileNames={lesson.resourceFileNames}
                        onDownload={onDownload}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default LessonModal;